import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { compressPhoto } from '../utils/imageCompress';
import { GALLERY_LIMITS, ITEM_KINDS, hasPrice, blobIdsOf } from '../constants/gallery';
import { planForUser } from '../constants/plans';
import useCredits from '../hooks/useCredits';
import { USER_TYPES } from '../constants/designer';
import { shareFromGalleryItem } from '../constants/share';
import {
  addGalleryItem, listGalleryItems, updateGalleryItem,
  setGalleryItemVisible, deleteGalleryItem, addItemImages, removeItemImage,
  getGalleryFull,
} from '../firebase/galleryService';
import GalleryItemMenu from './GalleryItemMenu';
import { DescriptionDialog, ConfirmDialog } from './GalleryDialogs';
import GalleryLightbox from './GalleryLightbox';
import ShareToChat from './ShareToChat';
import { fetchStats, SOCIAL_KINDS } from '../firebase/socialService';
import { unshareProjectAfterUnpublish } from '../firebase/workspaceService';
import './GalleryManager.css';

// Two rows of the three-across grid. Enough to show the work at a glance
// without a 60-item gallery pushing everything below it off the page.
const INITIAL_VISIBLE = 6;

// ── GALLERY MANAGER ──────────────────────────────────────────────────────────
// Upload + manage grid for a designer's published work. One component drives
// BOTH kinds (photos and 3D designs) because they carry the same controls —
// description, show/hide, delete — and only differ in which collection they
// live in. `kind` is the only thing that changes.
//
// Editing is optimistic: the tile updates immediately and reverts if the write
// fails. A gallery where every action waits on a round trip feels broken.
// `embedded` drops the card chrome so this can sit inside a tab panel.
// `allowUpload` is false for 3D designs — those are published from a project's
// own menu (where the project is known), not uploaded loose here.
export default function GalleryManager({
  kind = ITEM_KINDS.PHOTO, title, emptyHint,
  embedded = false, allowUpload = true, reloadKey = 0, onRequestUpload, onSummary,
  openItemId, onOpened,
}) {
  const { user, profile } = useAuth();
  const { t } = useT();
  const fileRef = useRef(null);

  // A shop's photos are product listings, so its dialog also asks for a price
  // and a buy link. This grid only ever shows the SIGNED-IN user's own items
  // (see the loader below), so their own role is the right thing to check.
  const isShop = profile?.userType === USER_TYPES.SHOP && kind === ITEM_KINDS.PHOTO;

  // ── HOW MANY THIS ACCOUNT MAY PUBLISH ──────────────────────────────────────
  // From the PLAN, which is the only place the number is written. It used to be
  // a flat GALLERY_LIMITS.maxItems of 60 for everybody, so `portfolioImages` in
  // constants/plans.js was never enforced: the pricing page sold 15 on free and
  // Unlimited on gold, and every account silently got 60. A gold designer was
  // being told "4/60" for a limit they had paid to remove.
  //
  // `Infinity` on gold compares correctly with >=, so the guards below need no
  // special case for unlimited and cannot forget one — that is exactly why the
  // plan record uses Infinity rather than null or a large number.
  //
  // From the LIVE plan, not from AuthContext's `profile`: that document is
  // fetched once per session, so an upgrade would leave the old cap in force
  // until a full reload. Same listener the header's balance already uses.
  //
  // PER KIND, as the flat limit was — photos and published designs each get
  // their own allowance rather than sharing one. That is what the number
  // replaced did, and halving everyone's effective space is not a fix.
  // WHICH LADDER, decided by the account type. A shop is not on the designer
  // tiers — it has no bronze, and it buys `productImages` rather than
  // `portfolioImages` — so reading the designer table for one would measure a
  // shop against a plan it was never sold. `planForUser` picks the table; the
  // field is picked on the line below it.
  //
  // `isShop` above is narrowed to PHOTO items, because that is where the shop
  // UI differs. The LIMIT applies to a shop whatever it is publishing, so this
  // reads userType directly rather than reusing that flag.
  const { plan } = useCredits();
  const shopAccount = profile?.userType === USER_TYPES.SHOP;
  const tier = planForUser({ plan, userType: profile?.userType });
  const maxItems = shopAccount ? tier.productImages : tier.portfolioImages;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Which tile the viewer is open on, as an index into `items`. -1 is closed.
  const [lightboxAt, setLightboxAt] = useState(-1);
  // Six is two rows of the three-across grid — enough to show the work without
  // a large gallery pushing everything else off the page.
  const [showAll, setShowAll] = useState(false);
  const [menuFor, setMenuFor]   = useState(null);  // tile id whose gear menu is open
  const [descFor, setDescFor]   = useState(null);  // item being described
  const [delFor,  setDelFor]    = useState(null);  // item awaiting delete confirmation
  const [toChat,  setToChat]    = useState(null);  // item being sent into a conversation

  // One timer per item id, so editing two descriptions doesn't have one cancel
  // the other's pending save.
  const saveTimers = useRef({});

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.uid) return;
      try {
        const rows = await listGalleryItems(kind, user.uid, true);   // owner view
        if (alive) setItems(rows);
      } catch (err) {
        if (alive) setError(err?.message || 'load failed');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      Object.values(saveTimers.current).forEach(clearTimeout);
    };
  }, [kind, user?.uid, reloadKey]);

  // ── view counts ────────────────────────────────────────────────────────
  // A photo's counters live on the photo; a published design's live on the
  // PROJECT behind it, which is what the share page increments.
  const statKeyOf = (it) => (kind === ITEM_KINDS.PHOTO ? it.id : it.projectId);

  const [stats, setStats] = useState({});
  const statIdKey = items.map(statKeyOf).filter(Boolean).join(',');
  useEffect(() => {
    if (!statIdKey) return undefined;
    let alive = true;
    // Static and batched, like every other grid — one query for the whole
    // gallery, no listener per tile.
    const sk = kind === ITEM_KINDS.PHOTO ? SOCIAL_KINDS.PHOTO : SOCIAL_KINDS.PROJECT;
    fetchStats(sk, statIdKey.split(','))
      .then(s => { if (alive) setStats(s); })
      .catch(() => {});
    return () => { alive = false; };
  }, [statIdKey, kind]);

  // ── deep link ──────────────────────────────────────────────────────────
  // Arrived from a like notification, which promises to show the liked item on
  // the owner's OWN page. A PHOTO opens in the lightbox; a published DESIGN is
  // scrolled to and briefly highlighted instead.
  //
  // That split is now a CHOICE rather than a limitation. It was written when a
  // design had no in-page viewer at all; the lightbox can open one today (see
  // openItem). What the notification says is "someone liked this", and landing
  // on the tile answers that in context — how many likes it has, what else is
  // in the gallery — where opening the scene answers a question nobody asked.
  // Worth revisiting if the two ever feel inconsistent.
  const [flashId, setFlashId] = useState(null);
  // The id this instance has already acted on. A BOOLEAN "have I opened yet"
  // was wrong twice over: clicking a second notification changed the id but
  // never fired again, and switching tabs remounted this component with a
  // fresh `false`, which re-opened the photo the URL still named.
  const openedRef = useRef(null);
  const flashTimer = useRef(null);
  useEffect(() => {
    // The parent clears the parameter once we have acted, which lands here as
    // an empty id. That is the moment to forget what we opened — otherwise
    // clicking the SAME notification a second time sets an id we still think
    // we have handled, and nothing happens.
    if (!openItemId) { openedRef.current = null; return undefined; }
    if (openedRef.current === openItemId || !items.length) return undefined;
    const at = items.findIndex(it => statKeyOf(it) === openItemId);
    if (at < 0) return;
    openedRef.current = openItemId;
    // Past the first six the tile is behind "show more", so reveal it first.
    if (at >= INITIAL_VISIBLE) setShowAll(true);

    if (kind === ITEM_KINDS.PHOTO) {
      setLightboxAt(at);
    } else {
      setFlashId(items[at].id);
      // One frame later the tile exists in the DOM to scroll to.
      requestAnimationFrame(() => {
        document.getElementById('gal-' + items[at].id)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      // The timer lives in a REF, cleared only on unmount. As a local with an
      // effect cleanup it never fired: `onOpened()` below clears the URL
      // parameter, which re-runs this effect, whose cleanup cancelled the very
      // timeout that was supposed to end the highlight — so the tile stayed
      // ringed forever.
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlashId(null), 2600);
    }

    // The link has been honoured, so the parent drops it from the URL. Leaving
    // it there made every later tab switch re-open the same photo.
    onOpened?.();
    return undefined;
  }, [openItemId, items, kind, onOpened]);

  // Only on unmount — see above for why this must not be tied to the effect.
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  // Report this tab's totals upward, so the tab strip can show them without
  // fetching the same rows a second time. Only the MOUNTED kind is known here
  // — the parent fills in the other one (see DashboardGallery).
  const likeTotal = items.reduce(
    (sum, it) => sum + (stats[statKeyOf(it)]?.likesCount || 0), 0);
  useEffect(() => {
    onSummary?.(kind, { count: items.length, likes: likeTotal });
  }, [kind, items.length, likeTotal, onSummary]);

  // ── COMPOSING A POST ──────────────────────────────────────────────────────
  // The "+" opens this dialog EMPTY — a placeholder where the picture will go,
  // and the file picker behind it. Nothing is written to Firestore until Save.
  //
  // The old order was the other way round: files uploaded first, a post was
  // created, and the dialog then asked what it was. That meant a shop pressing
  // Escape left a real document behind — hidden, unnamed, unpriced — which is
  // why publishing had to be gated on a price afterwards. Composing first
  // removes the half-made post entirely: cancel writes nothing.
  //
  // `composer` is null when closed, otherwise the draft: the compressed images
  // waiting to be posted.
  const [composer, setComposer] = useState(null);

  const openComposer = useCallback(() => {
    if (items.length >= maxItems) { setError(t('gal_err_max')); return; }
    setError('');
    setComposer({ images: [] });
  }, [items.length, maxItems, t]);

  // Hand the parent a way to start a post, so a button rendered elsewhere (the
  // '+' beside the tabs) can open the composer without owning any of it.
  //
  // DECLARED AFTER `openComposer`, and that ordering is load-bearing: the
  // dependency array is evaluated during render, so referencing a `const`
  // defined further down the function throws before the effect ever runs.
  useEffect(() => {
    onRequestUpload?.(openComposer);
  }, [onRequestUpload, openComposer]);

  // ── files chosen inside the composer ──────────────────────────────────────
  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    // ── ONE POST, UP TO FIVE PICTURES ────────────────────────────────────────
    // Files ADD to the draft rather than replacing it, so the "+" can be
    // pressed again to keep going — that is the flow in the composer: pick two,
    // look at them, pick two more. The cap counts what is already there.
    const MAX = GALLERY_LIMITS.imagesPerItem;
    const have = composer?.images.length || 0;
    const room = MAX - have;
    if (room <= 0) { setError(t('gal_err_images_max').replace('{n}', MAX)); return; }

    const picked = files.slice(0, room);
    if (files.length > room) setError(t('gal_err_images_max').replace('{n}', MAX));
    else setError('');

    setBusy(true);
    try {
      // Sequential, not Promise.all: each file is decoded and re-encoded on the
      // main thread, and doing several at once on a phone locks the UI.
      for (const file of picked) {
        try {
          const image = await compressPhoto(file);
          // Appended one at a time, so each thumbnail appears as it finishes
          // instead of the strip staying empty until the last file is done.
          setComposer(c => (c ? { ...c, images: [...c.images, image] } : c));
        } catch (err) {
          // ONE BAD FILE DOES NOT LOSE THE DRAFT. A HEIC the browser cannot
          // decode, or a 40MP photo that will not compress under the ceiling,
          // is skipped and named; the rest stay in the composer.
          setError(t(err?.message === 'too-large' ? 'gal_err_large' : 'gal_err_upload'));
        }
      }
    } finally {
      setBusy(false);
    }
  }

  // ── EDITING AN EXISTING POST'S PICTURES ───────────────────────────────────
  // A SECOND file input, because the composer's is bound to the draft. One
  // input serving both would need the handler to work out which dialog asked,
  // and getting that wrong appends a shop's new photo to a post they were only
  // looking at.
  //
  // These write IMMEDIATELY, unlike the composer's draft. The post exists; an
  // "unsaved picture" on something already published would be a second kind of
  // pending state to explain, and Cancel would have to mean two things.
  const editFileRef = useRef(null);

  // ── THE STRIP'S OTHER PICTURES ────────────────────────────────────────────
  // Only the COVER's thumbnail is on the metadata document, so editing a
  // multi-image post has nothing to draw the rest with. They are fetched here,
  // from the blob documents, when the dialog opens.
  //
  // It is the full rendition — there is no smaller copy stored — so a five-image
  // post costs about 800KB to open for editing. That is a real price and it is
  // paid deliberately: the earlier version drew numbered placeholders instead
  // and a shop editing its own post could not see which picture was which,
  // which is the one thing the strip exists to show. Only on edit, only past
  // the cover, and cached for the session.
  const [shotUrls, setShotUrls] = useState({});   // blobId -> url
  const shotCache = useRef({});

  useEffect(() => {
    if (!descFor) return undefined;
    const extra = blobIdsOf(descFor).slice(1).filter(b => !shotCache.current[b]);
    if (!extra.length) return undefined;

    let alive = true;
    (async () => {
      // Parallel: they are independent reads and the strip should fill in
      // together rather than left to right.
      await Promise.all(extra.map(async (bid) => {
        try {
          const url = await getGalleryFull(kind, bid);
          if (!url) return;
          shotCache.current[bid] = url;
          if (alive) setShotUrls(m => ({ ...m, [bid]: url }));
        } catch { /* a missing blob stays a spinner rather than an error */ }
      }));
    })();
    return () => { alive = false; };
  }, [descFor, kind]);

  /** Keeps the grid, and the open dialog, in step with a changed image list. */
  // `setItems` directly rather than through `patchLocal`, which is declared
  // further down: a dependency array is evaluated during render, so naming a
  // `const` from below this line throws before anything runs. Both setters are
  // stable, so the deps are genuinely empty.
  const applyBlobIds = useCallback((id, blobIds, thumbUrl = null) => {
    if (!blobIds) return;
    const patch = thumbUrl ? { blobIds, thumbUrl } : { blobIds };
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
    setDescFor(d => (d && d.id === id ? { ...d, ...patch } : d));
  }, []);

  async function handleEditFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const target = descFor;
    if (!files.length || !target) return;

    const room = GALLERY_LIMITS.imagesPerItem - blobIdsOf(target).length;
    if (room <= 0) {
      setError(t('gal_err_images_max').replace('{n}', GALLERY_LIMITS.imagesPerItem));
      return;
    }
    setError('');
    setBusy(true);
    try {
      const images = [];
      for (const file of files.slice(0, room)) {
        try {
          images.push(await compressPhoto(file));
        } catch (err) {
          setError(t(err?.message === 'too-large' ? 'gal_err_large' : 'gal_err_upload'));
        }
      }
      if (images.length) {
        applyBlobIds(target.id, await addItemImages(kind, target.id, user.uid, images));
      }
    } catch {
      setError(t('gal_err_save'));
    } finally {
      setBusy(false);
    }
  }

  async function detachImage(id, blobId) {
    setBusy(true);
    try {
      const res = await removeItemImage(kind, id, blobId);
      // null means the service refused — the last picture left, or a cover
      // whose replacement thumbnail could not be made.
      if (!res) { setError(t('gal_err_save')); return; }
      // `thumbUrl` comes back only when the COVER was the one removed and the
      // next picture was promoted. Applying it here is what keeps the grid
      // behind the dialog showing the post's new first image instead of the
      // one that has just been deleted.
      applyBlobIds(id, res.blobIds, res.thumbUrl);
    } catch {
      setError(t('gal_err_save'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Save from the composer — the ONLY place a post is created.
   *
   * Everything is validated by the dialog before this runs (at least one
   * picture, and for a shop a type, a colour and a price), so what arrives here
   * is a complete post and is published outright. The old two-step — create
   * hidden, publish once priced — existed only because the document was written
   * before anyone had been asked; there is nothing left for it to guard.
   */
  async function createPost(text, fields) {
    const images = composer?.images || [];
    if (!images.length) return;
    setBusy(true);
    try {
      const created = await addGalleryItem(kind, user.uid, images, {
        ...(fields || {}),
        description: text,
        visible: true,
      });
      setItems(prev => [created, ...prev]);
      setComposer(null);
      setError('');
    } catch (err) {
      setError(t(err?.message === 'too-large' ? 'gal_err_large' : 'gal_err_upload'));
    } finally {
      setBusy(false);
    }
  }

  // ONE VIEWER FOR BOTH KINDS. A published design opened /share in a new tab
  // once, because the lightbox could only show an image and a design is a
  // scene, not a picture. The lightbox now embeds that same read-only viewer in
  // a frame, so the new tab bought nothing and cost the page the owner was on —
  // and the profile grid does the same thing, which is what a gallery managed
  // here and a gallery seen by a visitor being the same component is for.
  function openItem(it) {
    setLightboxAt(items.indexOf(it));
  }

  // ── edits ─────────────────────────────────────────────────────────────────
  const patchLocal = useCallback((id, patch) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  // Saved once, from the dialog's Save button — the old inline field debounced
  // a write on every keystroke, which also meant Cancel had nothing to undo.
  //
  // `productFields` is null unless the owner is a shop, so a designer's save is
  // byte-for-byte the write it always was.
  async function handleDescription(id, value, productFields = null) {
    const next = value.slice(0, GALLERY_LIMITS.description);
    const patch = { description: next, ...(productFields || {}) };

    // PRICING A LISTING IS WHAT PUBLISHES IT. An upload is created hidden
    // because the price cannot be asked for until the file exists; the dialog
    // refuses to save without one, so arriving here with product fields means
    // the last thing holding it back is gone.
    //
    // Gated on the item having been unpriced, NOT on it being hidden — a shop
    // that deliberately hid a priced product and then edited its description
    // must not have it silently republished underneath them.
    const before = items.find(it => it.id === id);
    if (productFields && before && !hasPrice(before)) patch.visible = true;
    // The local copy takes the RAW form values so the tile updates instantly;
    // the write path is what normalises them (see serializeProductFields), and
    // the next load reads back the normalised shape.
    patchLocal(id, patch);                               // optimistic
    try {
      await updateGalleryItem(kind, id, patch);
    } catch {
      setError(t('gal_err_save'));
    }
  }

  async function handleVisible(id, visible) {
    // ── THE SECOND DOOR INTO PUBLISHING ──────────────────────────────────────
    // The upload flow gates an unpriced listing behind the details dialog, but
    // the gear menu's show/hide reaches the same act by another route — and it
    // published an unpriced product in one click, which made the requirement
    // decorative rather than real.
    //
    // It does NOT refuse silently and it does not disable the menu item: the
    // shop asked to publish, and the only thing standing in the way is one
    // field. So the request is honoured by opening the dialog that collects it,
    // which already explains why the item is held back and publishes it on
    // save. Wanting to publish IS how you get asked for the price.
    //
    // HIDING is never gated. Taking something down must always work.
    const it = items.find(x => x.id === id);
    if (visible && isShop && it && !hasPrice(it)) {
      setDescFor(it);
      return;
    }

    patchLocal(id, { visible });                       // optimistic
    try {
      await setGalleryItemVisible(kind, id, visible);
    } catch {
      patchLocal(id, { visible: !visible });           // revert on failure
      setError(t('gal_err_save'));
    }
  }

  async function handleDelete(id) {
    const snapshot = items;
    const gone = items.find(it => it.id === id);
    setItems(prev => prev.filter(it => it.id !== id));  // optimistic
    try {
      await deleteGalleryItem(kind, id);
      // Un-publishing must also revoke the public read it granted, or the
      // /share link keeps working for anyone who kept the URL. Only reverses
      // sharing that PUBLISHING enabled — a manual share is left alone.
      if (kind === ITEM_KINDS.DESIGN && gone?.projectId) {
        await unshareProjectAfterUnpublish(gone.projectId).catch(() => {});
      }
    } catch {
      setItems(snapshot);                               // put it back
      setError(t('gal_err_save'));
    }
  }

  if (loading) return null;

  return (
    <div className={embedded ? 'gal gal--embedded' : 'set-card gal'} style={embedded ? undefined : { marginTop: 20 }}>
      {!embedded && <div className="set-identity-name" style={{ marginBottom: 4 }}>{title}</div>}
      <p className="af-subtitle" style={{ margin: embedded ? '0 0 12px' : '0 0 16px' }}>
        {/* "4/60" states a limit. On a plan that has none there is no
            denominator to state, so it shows the count alone — printing
            "4/Infinity", or a made-up ceiling, would both be worse than saying
            nothing about a limit that does not exist. */}
        {t('gal_sub')}{' '}
        <span className="gal-count">
          {maxItems === Infinity ? items.length : `${items.length}/${maxItems}`}
        </span>
      </p>

      {error && (
        <div className="af-toast af-toast--error" role="alert">
          <span className="af-toast__icon">⚠</span>{error}
        </div>
      )}

      {allowUpload && (<button
        type="button"
        className="af-btn gal-upload"
        onClick={openComposer}
        disabled={busy || items.length >= maxItems}
      >
        {busy && <span className="af-spinner" />}
        {busy ? t('gal_uploading') : t('gal_add')}
      </button>)}
      {/* Two inputs, one per dialog — see the note on editFileRef. */}
      <input
        ref={fileRef} type="file" accept="image/*" multiple hidden
        onChange={handleFiles} disabled={busy}
      />
      <input
        ref={editFileRef} type="file" accept="image/*" multiple hidden
        onChange={handleEditFiles} disabled={busy}
      />

      {items.length === 0 ? (
        <p className="gal-empty">{emptyHint || t('gal_empty')}</p>
      ) : (
        <div className="gal-grid">
          {(showAll ? items : items.slice(0, INITIAL_VISIBLE)).map(it => (
            <div
              key={it.id}
              id={`gal-${it.id}`}
              className={`gal-item${it.visible ? '' : ' gal-item--hidden'}${flashId === it.id ? ' gal-item--flash' : ''}`}
            >
              <button
                type="button"
                className="gal-thumb"
                onClick={() => openItem(it)}
                aria-label={t('gal_open')}
              >
                {/* An unreadable thumbnail still renders its tile here — the
                    owner needs the gear menu to delete it. */}
                <img
                  src={it.thumbUrl}
                  alt={it.description || ''}
                  loading="lazy"
                  onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
                />
                {/* Bottom-left strip: whether it is hidden, and how many people
                    have looked at it. Views are the one number the owner can't
                    get anywhere else without opening every item in turn. */}
                <span className="gal-tag-row">
                  {/* WHY it is hidden, when the reason is a missing price.
                      "Hidden" alone reads as a choice the shop made, and the
                      fix — open the gear menu and set a price — is not
                      guessable from it. */}
                  {!it.visible && (
                    <span className={`gal-hidden-tag${isShop && !hasPrice(it) ? ' gal-hidden-tag--price' : ''}`}>
                      {isShop && !hasPrice(it) ? t('gal_needs_price') : t('gal_hidden')}
                    </span>
                  )}
                  {/* PUBLISHED BUT UNFINDABLE. Product search runs on `type`,
                      so a listing without one can be reached only by someone
                      already looking at this shop — which is every listing
                      published before the field existed. It shows and sells
                      fine, so it is not hidden; it just needs a type, and
                      nothing else on the tile would ever say so. */}
                  {isShop && it.visible && !it.type && (
                    <span className="gal-hidden-tag gal-hidden-tag--price">
                      {t('gal_needs_details')}
                    </span>
                  )}
                  {stats[statKeyOf(it)] && (
                    <span className="gal-views" title={t('ps_views')}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {stats[statKeyOf(it)].viewsCount || 0}
                    </span>
                  )}
                </span>
                {/* Description surfaces on hover, over the bottom of the image —
                    so the grid stays pure photos until you look closer. */}
                {it.description && (
                  <span className="gal-cap-overlay">{it.description}</span>
                )}
              </button>

              {/* Single gear replacing the old eye/textarea/delete row — the
                  controls move into a menu so the image gets the space. */}
              <button
                type="button"
                className="gal-gear"
                onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === it.id ? null : it.id); }}
                aria-haspopup="menu"
                aria-expanded={menuFor === it.id}
                aria-label={t('gal_settings')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.9"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
                    stroke="currentColor" strokeWidth="1.6"/>
                </svg>
              </button>

              {menuFor === it.id && (
                <GalleryItemMenu
                  item={it}
                  onClose={() => setMenuFor(null)}
                  onDescription={() => setDescFor(it)}
                  onToggleVisible={() => handleVisible(it.id, !it.visible)}
                  onDelete={() => setDelFor(it)}
                  onShareToChat={() => setToChat(it)}
                />
              )}

            </div>
          ))}
        </div>
      )}

      {/* Only appears when there is genuinely more to show — a button that
          reveals nothing is worse than no button. */}
      {!showAll && items.length > INITIAL_VISIBLE && (
        <button type="button" className="gal-more" onClick={() => setShowAll(true)}>
          {t('gal_show_more').replace('{n}', items.length - INITIAL_VISIBLE)}
        </button>
      )}

      {/* Lightbox shows the THUMB, not the full rendition: the full image lives
          in a separate document and is only worth fetching on a public profile
          view. Here the owner just needs to recognise which photo is which. */}
      {lightboxAt >= 0 && items[lightboxAt] && (
        <GalleryLightbox
          item={items[lightboxAt]}
          kind={kind}
          onClose={() => setLightboxAt(-1)}
        />
      )}

      {/* ── THE COMPOSER ── a new post, not yet written anywhere. It owns the
          pictures; the same dialog in edit mode (below) owns only the words. */}
      {composer && (
        <DescriptionDialog
          item={{}}
          product={isShop}
          composing
          // Drafts: every one has a thumbnail and every one can go, including
          // the first — nothing is published, so there is no cover to protect.
          shots={composer.images.map((img, i) => ({
            key: `draft-${i}-${img.thumbUrl.slice(-24)}`,
            thumbUrl: img.thumbUrl,
            removable: true,
            index: i,
          }))}
          busy={busy}
          maxImages={GALLERY_LIMITS.imagesPerItem}
          onAddImages={() => fileRef.current?.click()}
          onRemoveShot={(shot) => setComposer(c => (
            c ? { ...c, images: c.images.filter((_, j) => j !== shot.index) } : c
          ))}
          onSave={createPost}
          // Closing discards the draft. Nothing was written, so there is
          // nothing to undo — which is the whole point of composing first.
          onClose={() => { setComposer(null); setError(''); }}
        />
      )}

      {descFor && (
        <DescriptionDialog
          // Keyed on the item, so opening a different one REMOUNTS the dialog.
          // Without this its draft state is initialised once and the next
          // product opens holding the previous one's price.
          key={descFor.id}
          item={descFor}
          product={isShop}
          // The post's pictures, in order. The cover's thumbnail is on the
          // metadata document; the rest are fetched above and show a spinner
          // until they land.
          //
          // ANY of them can go, cover included — removing the first promotes
          // the second and rebuilds the thumbnail every grid draws the post
          // with (see removeItemImage). The ONLY one held back is the last
          // remaining picture: a post is its pictures, and deleting the post is
          // how you remove that one.
          shots={blobIdsOf(descFor).map((bid, i) => ({
            key: bid,
            thumbUrl: i === 0 ? descFor.thumbUrl : (shotUrls[bid] || null),
            removable: blobIdsOf(descFor).length > 1,
            blobId: bid,
          }))}
          busy={busy}
          maxImages={GALLERY_LIMITS.imagesPerItem}
          onAddImages={() => editFileRef.current?.click()}
          onRemoveShot={(shot) => detachImage(descFor.id, shot.blobId)}
          onSave={(text, fields) => handleDescription(descFor.id, text, fields)}
          onClose={() => setDescFor(null)}
        />
      )}

      {delFor && (
        <ConfirmDialog
          title={t('gal_delete')}
          message={t('gal_delete_confirm')}
          confirmLabel={t('gal_yes')}
          onConfirm={() => handleDelete(delFor.id)}
          onClose={() => setDelFor(null)}
        />
      )}

      {/* Send this piece of work to someone as a card in a conversation. The
          tile's own thumbnail is the source; ShareToChat re-encodes it down to
          card size once, rather than putting a 640px grid image on a message
          every reader of the thread pays to load. */}
      {toChat && (
        <ShareToChat
          share={shareFromGalleryItem(toChat, kind)}
          thumbSrc={toChat.thumbUrl || ''}
          onClose={() => setToChat(null)}
        />
      )}
    </div>
  );
}
